import { useEffect, useState } from 'react';
import { loadDailyCommissions, type DailyPoint } from '../../lib/dailyCommissions';
import { COMMISSION_CENTS } from '../../lib/networkOverview';
import { euros } from '../../lib/performance';

/**
 * Graphique « commissions Stripe — N derniers jours » (parité prod legacy).
 * Histogramme SVG inline (aucune lib). Se masque proprement si la RPC
 * `daily_commissions` n'est pas dispo ou si la série est vide.
 */
export default function NetworkCommissionsChart({ days = 30 }: { days?: number }) {
  const [series, setSeries] = useState<DailyPoint[] | null>(null);

  useEffect(() => {
    loadDailyCommissions(days).then(setSeries);
  }, [days]);

  // Chargement ou indisponible → on ne rend rien (pas de bloc vide).
  if (series === null) return null;
  const total = series.reduce((s, p) => s + p.onlineOrders, 0);
  if (series.length === 0 || total === 0) return null;

  const maxOrders = Math.max(...series.map((p) => p.onlineOrders), 1);
  const W = 720;
  const H = 120;
  const gap = 2;
  const barW = Math.max((W - gap * (series.length - 1)) / series.length, 1);
  const periodCents = total * COMMISSION_CENTS;

  return (
    <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4">
      <div className="mb-1 font-bold">CA commissions Stripe — {days} derniers jours</div>
      <div className="mb-3 text-xs text-slate-500">Hors abonnements · {euros(periodCents)} sur la période</div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label={`Commissions sur ${days} jours`}>
        {series.map((p, i) => {
          const h = (p.onlineOrders / maxOrders) * (H - 4);
          const x = i * (barW + gap);
          const cents = p.onlineOrders * COMMISSION_CENTS;
          return (
            <rect
              key={p.day}
              x={x}
              y={H - h}
              width={barW}
              height={h}
              rx={1}
              fill="#2563EB"
              opacity={p.onlineOrders > 0 ? 0.85 : 0.15}
            >
              <title>{`${p.day} · ${p.onlineOrders} cmd · ${euros(cents)}`}</title>
            </rect>
          );
        })}
      </svg>
      <div className="mt-1 flex justify-between text-[10px] text-slate-400">
        <span>{series[0]?.day.slice(5)}</span>
        <span>aujourd'hui</span>
      </div>
    </div>
  );
}
